import { ChangeDetectionStrategy, Component, ViewEncapsulation, computed, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, RouterLink } from '@angular/router';

import { filter, map, startWith } from 'rxjs/operators';

import { ENVIRONMENT } from '@app/constants/environment.token';
import { UtilsService } from '@app/services/utils.service';

/** Identifiers for the five bottom-bar sections. */
export type BottomMenuSection = 'home' | 'msb4you' | 'shop' | 'programs' | 'myarea';

/**
 * BottomMenuComponent — fixed bottom navigation bar for mobile.
 * Five items: Home, MSB4You, Shop, Programmi, My Area.
 * Internal Angular routes use RouterLink; external WordPress paths use [href].
 *
 * @example
 * <mst-bottom-menu />
 */
@Component({
    selector: 'mst-bottom-menu',
    templateUrl: './bottom-menu.component.html',
    styleUrl: './bottom-menu.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    standalone: true,
    imports: [RouterLink],
    host: {
        class: 'mst-bottomMenu stack row yStretch xBetween px16 py12 fixed bottom0 left0 hide-l',
        role: 'navigation',
        'aria-label': 'Navigazione principale mobile',
    },
})
export class BottomMenuComponent {
    private readonly router = inject(Router);
    private readonly env = inject(ENVIRONMENT);
    private readonly utilsService = inject(UtilsService);

    /** WordPress base URL for external links (MSB4You, Shop, Programmi). */
    readonly wpUrl = this.env.wpUrl;

    /**
     * True when the app is running in PWA standalone mode (installed on home screen).
     * In this mode, external WordPress links are replaced by internal Angular routes.
     */
    readonly isStandaloneMode = this.utilsService.isInStandaloneMode();

    /** Resolved current URL, updated on every NavigationEnd event. */
    private readonly currentUrl = toSignal(
        this.router.events.pipe(
            filter((e) => e instanceof NavigationEnd),
            map((e) => (e as NavigationEnd).urlAfterRedirects),
            startWith(this.router.url),
        ),
        { initialValue: this.router.url },
    );

    /**
     * Determines which bottom-bar section is active based on the current URL.
     * My Area is active on all authenticated sub-routes (/training, /gym, /nutrition, /progress).
     */
    readonly activeSection = computed<BottomMenuSection>(() => {
        const url = this.currentUrl();
        if (url.startsWith('/home') || url === '/') return 'home';
        if (url.includes('msb4you')) return 'msb4you';
        if (url.includes('shop')) return 'shop';
        if (url.includes('programs')) return 'programs';
        // My Area covers the entire authenticated section
        if (
            url.startsWith('/login') ||
            url.startsWith('/password-recover') ||
            url.startsWith('/password-reset') ||
            url.startsWith('/training') ||
            url.startsWith('/gym') ||
            url.startsWith('/nutrition') ||
            url.startsWith('/progress')
        ) {
            return 'myarea';
        }
        return 'home';
    });
}
